import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'ws-scripts/modules/common';
import { useDrawerNavigation } from '../context/DrawerNavigationContext';
import useFocusTrap from '../hooks/useFocusTrap';
import { SlidePanel } from './SlidePanel';
import { Backdrop } from './Backdrop';
import DrawerPanelBody from './DrawerPanelBody';

/** Fallback aria-label for the main navigation drawer panel. */
export const NAV_DRAWER_ARIA_LABEL = 'Navigation menu';

/**
 * MainPanel — wraps the main drawer SlidePanel with useFocusTrap.
 *
 * Uses the keep-mounted SlidePanel (not react-bootstrap Modal) so the main
 * panel's DOM survives close/reopen instead of being torn down and rebuilt.
 * Extracted so useFocusTrap can read activeDrillDownItem from
 * DrawerNavigationContext (which is provided above this component).
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.closeDrawer
 * @param {Function} props.handleExited
 * @param {Function} props.handleEntered
 * @param {string} props.dockClass
 * @param {boolean} [props.isMobile]
 * @param {string} [props.title]
 * @param {React.RefObject} [props.closeRef]
 * @returns {React.ReactElement}
 */
const MainPanel = ({
	isOpen,
	closeDrawer,
	handleExited,
	handleEntered,
	dockClass,
	title,
	closeRef,
	isMobile
}) => {
	const panelRef = useRef(null);
	const labels = useLabels();
	const { activeDrillDownItem } = useDrawerNavigation();

	// The main panel is the active surface only while it is open AND no
	// drilldown sits on top of it. While a drilldown is open the main panel is
	// inert (removed from the tab order + a11y tree) so the focus trap, Escape,
	// and screen-reader focus all belong to the drilldown, not this panel.
	const isActive = isOpen && activeDrillDownItem === null;

	useFocusTrap(panelRef, isActive);

	return (
		<SlidePanel
			isOpen={isOpen}
			dockClass={dockClass}
			onClose={closeDrawer}
			onExited={handleExited}
			onEntered={handleEntered}
			isActive={isActive}
			className={isMobile ? 'ws-nav-drawer-modal--mobile' : ''}
			ariaLabel={labels.get('NAV_DRAWER_LABEL') || NAV_DRAWER_ARIA_LABEL}
			backdrop={<Backdrop show={isOpen} />}
		>
			<div
				id="ws-nav-drawer-main-panel"
				className="ws-nav-drawer__panel-inner d-flex flex-column h-100"
				data-testid="ws-nav-drawer-panel-inner"
				ref={panelRef}
			>
				<DrawerPanelBody title={title} closeRef={closeRef} />
			</div>
		</SlidePanel>
	);
};

MainPanel.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	closeDrawer: PropTypes.func.isRequired,
	handleExited: PropTypes.func.isRequired,
	handleEntered: PropTypes.func.isRequired,
	dockClass: PropTypes.string.isRequired,
	isMobile: PropTypes.bool,
	title: PropTypes.string,
	closeRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
};

MainPanel.defaultProps = {
	isMobile: false,
	title: '',
	closeRef: null
};

export { MainPanel };
